import { useEffect, useRef } from 'react';

const HEIGHT_MEASUREMENT_EPSILON_PX = 4;

/**
 * Observes a DOM element's border-box height and reports meaningful changes
 * via updateNodeData. Prevents ResizeObserver noise by deduplicating heights
 * within an epsilon tolerance.
 *
 * @param contentRef - Ref to the DOM element to observe
 * @param nodeId - React Flow node ID to update
 * @param isExpanded - Whether the node is currently expanded (observation paused when collapsed)
 * @param initialHeight - Initial height estimate (used to seed the dedup ref)
 * @param updateNodeData - Callback to persist the measured height into the node's data
 */
export function useNodeHeightMeasurement(
	contentRef: React.RefObject<HTMLDivElement | null>,
	nodeId: string,
	isExpanded: boolean,
	initialHeight: number | null,
	updateNodeData: ((nodeId: string, data: Record<string, unknown>) => void) | undefined
): void {
	const measuredHeightRef = useRef<number | null>(initialHeight);

	useEffect(() => {
		if (!contentRef.current || !isExpanded) {
			if (!isExpanded) {
				measuredHeightRef.current = null;
			}
			return;
		}

		const resizeObserver = new ResizeObserver(entries => {
			for (const entry of entries) {
				const borderBox = entry.borderBoxSize?.[0];
				const actualHeight = Math.round(borderBox ? borderBox.blockSize : entry.contentRect.height);
				if (actualHeight <= 0) {
					continue;
				}

				const previousHeight = measuredHeightRef.current;
				if (previousHeight !== null && Math.abs(previousHeight - actualHeight) <= HEIGHT_MEASUREMENT_EPSILON_PX) {
					continue;
				}

				measuredHeightRef.current = actualHeight;
				updateNodeData?.(nodeId, { estimatedHeight: actualHeight });
			}
		});

		resizeObserver.observe(contentRef.current);

		return () => {
			resizeObserver.disconnect();
		};
	}, [contentRef, nodeId, isExpanded, updateNodeData]);
}
